/**
 * Sessions column for matching (list, filters, calendar sync).
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calendar } from 'lucide-react';
import { MatchingCollapse } from './MatchingCollapse';
import { MatchingRow } from './MatchingRow';
import { ScrollBar } from '@/components/ui/scroll-bar';
import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/forms/Button';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { SessionFilters as SessionFiltersContent } from '@/components/sessions/inperson/SessionFilters';
import { FilterConfigurePanel } from '@/components/common/data-table/FilterConfigurePanel';
import { getSessions } from '@/services/inperson/sessions.service';
import { useFilterVisibility } from '@/hooks/useFilterVisibility';
import { cn } from '@/lib/utils';
import {
  MATCHING_SESSION_FILTER_GROUP_IDS,
  MATCHING_SESSION_FILTER_GROUP_LABELS,
  type MatchingSessionColumnFilters,
  type MatchingSessionFilterGroupId,
} from '@/components/pairs/pair-configs';
import type { SessionForMatching } from '@/types/inperson/matching.types';
import type { UserWithProfile } from '@/types/user.types';
import {
  buildMatchingSessionsListParams,
  buildSelectedDatesKey,
  calendarDatesForMatchingDateRange,
  formatMatchingSessionCountLabel,
  formatMatchingSessionPageSuffix,
  getMatchingSessionMaxStudents,
  getTodayMatchingSessionFilters,
  hasActiveMatchingSessionFilters,
  matchingSessionFiltersFromCustomRange,
  matchingSessionFiltersFromDateRange,
  matchingSessionFiltersFromSelectedDates,
  resolveSessionDisplayCounts,
  toggleListValue,
  toSessionForMatching,
} from '../matching-utils';

/** Callbacks registered by each {@link MatchingCollapse} for drag-and-drop. */
export type SessionHandlers = {
  handleAddTutor: (user: UserWithProfile) => void;
  handleAddStudent: (user: UserWithProfile) => void;
  handleMatchStudentToTutor: (studentId: string, tutorId: string) => void;
  handleUnmatchStudent: (studentId: string, tutorId: string) => void;
  getSpecificMatches: () => Array<{ studentId: string; tutorId: string }>;
  getTempTutors: () => UserWithProfile[];
  getTempStudents: () => UserWithProfile[];
};

/** Props for {@link MatchingTable}. */
interface MatchingTableProps {
  selectedDates?: Date[];
  onRegisterSessionHandlers: (sessionId: string, handlers: SessionHandlers) => void;
  onSessionToggled: (expandedSessionId: string | null) => void;
  onParticipantsChange: () => void;
  onRequestCreateSession: () => void;
  onSetSelectedDates?: (dates: Date[]) => void;
  className?: string;
}

/** Center column: matching sessions list, filters, and calendar sync. */
export const MatchingTable: React.FC<MatchingTableProps> = ({
  selectedDates = [],
  onRegisterSessionHandlers,
  onSessionToggled,
  onParticipantsChange,
  onRequestCreateSession,
  onSetSelectedDates,
  className,
}) => {
  const [sessionsPage, setSessionsPage] = React.useState(1);
  const sessionsPerPage = 10;
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc');
  const [filters, setFilters] = React.useState<MatchingSessionColumnFilters>(
    getTodayMatchingSessionFilters(),
  );
  const [showFilters, setShowFilters] = React.useState(false);
  const [showConfigure, setShowConfigure] = React.useState(false);

  const {
    visibleFilterIds,
    allowedFilterIds,
    filterVisibility,
    handleToggleFilterVisibility,
    isSavingFilterVisibility,
  } = useFilterVisibility<MatchingSessionFilterGroupId>({
    preferencesName: 'sessions-matching',
    elementsName: 'sessions',
    allFilterGroupIds: MATCHING_SESSION_FILTER_GROUP_IDS,
  });

  const configureContent = (
    <FilterConfigurePanel
      allowedFilterIds={allowedFilterIds}
      filterVisibility={filterVisibility}
      filterGroupLabels={MATCHING_SESSION_FILTER_GROUP_LABELS}
      onToggle={handleToggleFilterVisibility}
      isSaving={isSavingFilterVisibility}
    />
  );

  /** Ignore the next {@link selectedDates} change after we update the calendar from a filter. */
  const isProgrammaticCalendarUpdate = React.useRef(false);
  /** Stable key so calendar sync does not fire on same-day `Date` identity. */
  const selectedDatesKey = React.useMemo(
    () => buildSelectedDatesKey(selectedDates ?? []),
    [selectedDates],
  );

  /** Calendar → filters: today preset, or a custom inclusive range. Skip programmatic writes. */
  React.useEffect(() => {
    if (isProgrammaticCalendarUpdate.current) {
      isProgrammaticCalendarUpdate.current = false;
      return;
    }
    if (selectedDates && selectedDates.length > 0) {
      setFilters((prev) => ({
        ...prev,
        ...matchingSessionFiltersFromSelectedDates(selectedDates),
      }));
    }
  }, [selectedDatesKey]);

  /** Default the calendar to today on mount (flag avoids echoing back into filters). */
  React.useEffect(() => {
    isProgrammaticCalendarUpdate.current = true;
    onSetSelectedDates?.(calendarDatesForMatchingDateRange('today'));
  }, []);

  const [expandedSessions, setExpandedSessions] = React.useState<Set<string>>(new Set());
  const [participantCounts, setParticipantCounts] = React.useState<
    Record<string, { tutorCount: number; studentCount: number }>
  >({});

  /** Parent columns need the single expanded session id (or null). */
  React.useEffect(() => {
    const nextId = expandedSessions.size === 1 ? Array.from(expandedSessions)[0] : null;
    onSessionToggled(nextId ?? null);
  }, [expandedSessions, onSessionToggled]);

  /** Accordion: expanding one session collapses the others. */
  const toggleSession = React.useCallback((sessionId: string) => {
    setExpandedSessions((prev) => {
      const newSet = new Set<string>();
      if (!prev.has(sessionId)) {
        newSet.add(sessionId);
      }
      return newSet;
    });
  }, []);

  /** Matching only lists `PLANNED` / `IN_PROGRESS` sessions. */
  const { data: sessionsResponse, isLoading: sessionsLoading } = useQuery({
    queryKey: [
      'sessions-matching',
      {
        page: sessionsPage,
        pageSize: sessionsPerPage,
        dateRange: filters.dateRange,
        types: filters.types,
        statuses: filters.statuses,
        establishmentId: filters.establishmentId,
        subjectId: filters.subjectId,
        levelId: filters.levelId,
        search: filters.search,
        customDateFrom: filters.customDateFrom?.getTime(),
        customDateTo: filters.customDateTo?.getTime(),
        sortOrder,
      },
    ],
    queryFn: async () => {
      return await getSessions(
        buildMatchingSessionsListParams(filters, {
          page: sessionsPage,
          pageSize: sessionsPerPage,
          sortOrder,
        }),
      );
    },
  });

  const sessionsData = sessionsResponse?.data || [];
  const totalSessions = sessionsResponse?.total || 0;

  /** List-API sessions → matching session shape (typed sources + profiles). */
  const convertedSessions = React.useMemo(
    () => sessionsData.map(toSessionForMatching),
    [sessionsData],
  );

  const totalSessionsPages = Math.ceil(totalSessions / sessionsPerPage);
  const canGoToPreviousPage = sessionsPage > 1;
  const canGoToNextPage = sessionsPage < totalSessionsPages;

  /** Toggle a session type and reset to page 1. */
  const toggleTypeFilter = React.useCallback((type: string) => {
    setFilters((prev) => ({
      ...prev,
      types: toggleListValue(prev.types, type),
    }));
    setSessionsPage(1);
  }, []);

  /** Toggle a session status and reset to page 1. */
  const toggleStatusFilter = React.useCallback((status: string) => {
    setFilters((prev) => ({
      ...prev,
      statuses: toggleListValue(prev.statuses, status),
    }));
    setSessionsPage(1);
  }, []);

  /** Preset range also updates the calendar (`today` = one day, else clear). */
  const setDateRange = React.useCallback(
    (range: MatchingSessionColumnFilters['dateRange']) => {
      setFilters((prev) => ({
        ...prev,
        ...matchingSessionFiltersFromDateRange(range),
      }));
      isProgrammaticCalendarUpdate.current = true;
      onSetSelectedDates?.(calendarDatesForMatchingDateRange(range));
      setSessionsPage(1);
    },
    [onSetSelectedDates],
  );

  /** Custom dates force `dateRange` to `all` so the preset does not override them. */
  const setCustomDateRange = React.useCallback((from: Date | undefined, to: Date | undefined) => {
    setFilters((prev) => ({
      ...prev,
      ...matchingSessionFiltersFromCustomRange(from, to),
    }));
    setSessionsPage(1);
  }, []);

  const setEstablishment = React.useCallback((id: string | undefined) => {
    setFilters((prev) => ({ ...prev, establishmentId: id }));
    setSessionsPage(1);
  }, []);

  const setSubject = React.useCallback((id: string | undefined) => {
    setFilters((prev) => ({ ...prev, subjectId: id }));
    setSessionsPage(1);
  }, []);

  const setLevel = React.useCallback((id: string | undefined) => {
    setFilters((prev) => ({ ...prev, levelId: id }));
    setSessionsPage(1);
  }, []);

  const setSearch = React.useCallback((value: string | undefined) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setSessionsPage(1);
  }, []);

  /** Defaults plus today; also snap the calendar to today. */
  const clearFilters = React.useCallback(() => {
    setFilters(getTodayMatchingSessionFilters());
    isProgrammaticCalendarUpdate.current = true;
    onSetSelectedDates?.(calendarDatesForMatchingDateRange('today'));
    setSessionsPage(1);
  }, [onSetSelectedDates]);

  /** Session filter body reused by the modal `customRender`. */
  const renderSessionFilters = React.useCallback(
    () => (
      <SessionFiltersContent
        filters={filters}
        allowedFilterIds={allowedFilterIds}
        visibleFilterIds={visibleFilterIds}
        onToggleType={toggleTypeFilter}
        onToggleStatus={toggleStatusFilter}
        onSetDateRange={setDateRange}
        onSetCustomDateRange={setCustomDateRange}
        onSetEstablishment={setEstablishment}
        onSetSubject={setSubject}
        onSetLevel={setLevel}
        onSetSearch={setSearch}
      />
    ),
    [
      filters,
      allowedFilterIds,
      visibleFilterIds,
      toggleTypeFilter,
      toggleStatusFilter,
      setDateRange,
      setCustomDateRange,
      setEstablishment,
      setSubject,
      setLevel,
      setSearch,
    ],
  );

  /** Single custom group wrapping {@link SessionFiltersContent}. */
  const filterGroups = React.useMemo(
    () => [
      {
        id: 'sessions-filters',
        label: 'Filtres des séances',
        type: 'custom' as const,
        customRender: renderSessionFilters,
      },
    ],
    [renderSessionFilters],
  );

  /** Badge / reset: any type, date, site, subject, level, or search besides the default. */
  const hasActiveFilters = React.useMemo(() => hasActiveMatchingSessionFilters(filters), [filters]);

  function handleParticipantCountsChange(
    sessionId: string,
    tutorCount: number,
    studentCount: number,
  ): void {
    setParticipantCounts((prev) => {
      const current = prev[sessionId];
      if (current?.tutorCount === tutorCount && current.studentCount === studentCount) {
        return prev;
      }
      return { ...prev, [sessionId]: { tutorCount, studentCount } };
    });
  }

  function getSessionTutorCount(session: SessionForMatching): number {
    return resolveSessionDisplayCounts(session, participantCounts[session.id]).tutorCount;
  }

  function getSessionStudentCount(session: SessionForMatching): number {
    return resolveSessionDisplayCounts(session, participantCounts[session.id]).studentCount;
  }

  function renderSession(session: SessionForMatching): React.ReactNode {
    const isExpanded = expandedSessions.has(session.id);
    function handleToggle(): void {
      toggleSession(session.id);
    }
    function handleCountsChange(tutorCount: number, studentCount: number): void {
      handleParticipantCountsChange(session.id, tutorCount, studentCount);
    }
    return (
      <div key={session.id} className="matching-collapse">
        <MatchingRow
          session={session}
          isExpanded={isExpanded}
          onToggle={handleToggle}
          tutorCount={getSessionTutorCount(session)}
          studentCount={getSessionStudentCount(session)}
          maxStudents={getMatchingSessionMaxStudents(session)}
        />
        <MatchingCollapse
          session={session}
          isExpanded={isExpanded}
          onRegisterHandlers={onRegisterSessionHandlers}
          onParticipantsChange={onParticipantsChange}
          onCountsChange={handleCountsChange}
        />
      </div>
    );
  }

  function renderSessionList(): React.ReactNode {
    if (sessionsLoading) return null;
    const rows: React.ReactNode[] = [];
    for (const session of convertedSessions) {
      rows.push(renderSession(session));
    }
    return rows;
  }

  return (
    <div className={cn('matching-column matching-column-center', className)}>
      <div className="matching-column-header">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center border"
              style={{
                background: 'color-mix(in srgb, var(--color-1-nuance-1) 10%, transparent)',
                borderColor: 'var(--color-1-nuance-1)',
              }}
            >
              <Calendar className="w-4 h-4" style={{ color: 'var(--color-1-nuance-1)' }} />
            </div>
            <div>
              <h3 className="text-base font-semibold" style={{ color: 'var(--color-1-nuance-1)' }}>
                Séances
              </h3>
              <p className="text-xs text-muted-foreground">
                {formatMatchingSessionCountLabel(totalSessions)}
                {formatMatchingSessionPageSuffix(sessionsPage, totalSessionsPages)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <Button
              preset="toolbar-create"
              tooltipText="Planification des séances"
              onClick={onRequestCreateSession}
            />
            <Button
              preset="toolbar-filter"
              tooltipText="Filtrer les séances"
              showBadge={hasActiveFilters}
              onClick={() => setShowFilters(true)}
            />
            <Button
              preset="toolbar-sort"
              tooltipText={
                sortOrder === 'desc'
                  ? 'Trier du plus ancien au plus récent'
                  : 'Trier du plus récent au plus ancien'
              }
              onClick={() => setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
              disabled={sessionsLoading}
            />
            <Button
              preset="column-pagination-prev"
              onClick={() => setSessionsPage((p) => p - 1)}
              disabled={!canGoToPreviousPage || sessionsLoading}
            />
            <Button
              preset="column-pagination-next"
              onClick={() => setSessionsPage((p) => p + 1)}
              disabled={!canGoToNextPage || sessionsLoading}
            />
          </div>
        </div>
      </div>

      <ScrollBar className="matching-list">
        {sessionsLoading && (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" label="Chargement des séances..." />
          </div>
        )}

        {!sessionsLoading && convertedSessions.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center border"
              style={{
                background: 'color-mix(in srgb, var(--color-1-nuance-3) 10%, transparent)',
                borderColor: 'var(--color-1-nuance-3)',
              }}
            >
              <Calendar className="w-6 h-6" style={{ color: 'var(--color-1-nuance-3)' }} />
            </div>
            <p className="text-sm text-muted-foreground text-center">
              Aucune séance planifiée ou en cours
            </p>
          </div>
        )}

        {renderSessionList()}
      </ScrollBar>

      <DataTableFilters
        show={showFilters}
        mode="modal"
        onClose={() => {
          setShowFilters(false);
          setShowConfigure(false);
        }}
        hasActiveFilters={hasActiveFilters}
        filterGroups={filterGroups}
        onClearFilters={clearFilters}
        resultCount={totalSessions}
        resultLabel="séances"
        onToggleConfigure={() => setShowConfigure((v) => !v)}
        showConfigure={showConfigure}
        configureContent={configureContent}
      />
    </div>
  );
};
